import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
// Both ship inside dayjs — no new dependency. Needed because a posting slot is
// wall-clock in the *workspace's* zone, and rendering it in the browser's would
// tell someone in London that their Dhaka 9am post goes out at 3am.
dayjs.extend(utc);
dayjs.extend(timezone);

/** Backend timestamps are epoch milliseconds throughout. */
export function formatDateTime(ms?: number | null): string {
  if (!ms) return '—';
  return dayjs(ms).format('D MMM YYYY, HH:mm');
}

export function formatDate(ms?: number | null): string {
  if (!ms) return '—';
  return dayjs(ms).format('D MMM YYYY');
}

export function formatRelative(ms?: number | null): string {
  if (!ms) return '—';
  return dayjs(ms).fromNow();
}

/* ─── Workspace-timezone rendering ──────────────────────────────────────────
 *
 * Everything above renders in the browser's zone, which is right for "when did
 * this happen". Everything below renders in the workspace's, which is the only
 * correct answer for "when will this post" — the schedule was written in those
 * terms, and the browser's zone is an accident of where someone happens to be.
 *
 * A workspace with no zone set falls back to UTC rather than to the browser,
 * so the label never claims a zone the schedule was not written in.
 */

export function workspaceZone(zone?: string | null): string {
  if (!zone) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    // `workspaces.timezone` is free text with no validation behind it.
    return 'UTC';
  }
}

/** e.g. "Mon 5 Jan, 09:00". Always paired with a visible zone label. */
export function formatInZone(ms?: number | null, zone?: string | null): string {
  if (!ms) return '—';
  return dayjs(ms).tz(workspaceZone(zone)).format('ddd D MMM, HH:mm');
}

/** Just the time, for a list already grouped by day. */
export function formatTimeInZone(
  ms?: number | null,
  zone?: string | null
): string {
  if (!ms) return '—';
  return dayjs(ms).tz(workspaceZone(zone)).format('HH:mm');
}

/** `YYYY-MM-DD` in the workspace's zone, for grouping. */
export function localDayInZone(ms: number, zone?: string | null): string {
  return dayjs(ms).tz(workspaceZone(zone)).format('YYYY-MM-DD');
}

/** Minutes since local midnight → "09:00". Matches `postingSlot.timeOfDay`. */
export function formatTimeOfDay(minutes: number): string {
  const hour = String(Math.floor(minutes / 60)).padStart(2, '0');
  const minute = String(minutes % 60).padStart(2, '0');
  return `${hour}:${minute}`;
}

/** ISO weekday 1–7 → "Mon". Matches `postingSlot.dayOfWeek`. */
export const ISO_WEEKDAYS = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const;

export function formatWeekday(isoWeekday: number): string {
  return ISO_WEEKDAYS[isoWeekday - 1] ?? '—';
}

export function formatBytes(bytes?: number | null): string {
  if (!bytes) return '—';

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** A clip's length the way players show it: `0:12`, `4:05`, `1:02:30`. */
export function formatClipDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '';
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}`
    : `${minutes}:${secs}`;
}

export function formatNumber(value?: number | null): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * AI costs are fractions of a cent per call, so a plain 2-decimal currency
 * format would render almost everything as $0.00.
 */
export function formatCost(value?: number | null, currency = 'USD'): string {
  if (value === null || value === undefined) return '—';
  if (value === 0) return `${currencySymbol(currency)}0`;

  const decimals = value < 0.01 ? 4 : value < 1 ? 3 : 2;
  return `${currencySymbol(currency)}${value.toFixed(decimals)}`;
}

function currencySymbol(currency: string): string {
  const symbols: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
  return symbols[currency] ?? `${currency} `;
}

/** Strip HTML to a readable one-liner for list previews. */
export function toPlainPreview(html: string, maxLength = 160): string {
  const text = html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 200)
    .replace(/^-|-$/g, '');
}
